<script setup lang="ts">
import { defineAsyncComponent, h, shallowRef, useSSRContext, watch } from 'vue'

const props = defineProps<{ name: string }>()

const aliases: Record<string, string> = {
  accidents: 'shield-user',
  accounting: 'calculator',
  agrocredit: 'tractor',
  allday: 'haze',
  allday_support: 'headset',
  auto: 'autos_support',
  autos: 'autos_support',
  aviation: 'plane',
  'bank-guarantee': 'vault',
  best: 'mfk',
  business: 'mka',
  business_expansion: 'mka',
  buying_car: 'autos_support',
  capitalization: 'deposits',
  'car-front': 'autos_support',
  cards: 'zolotaya-korona',
  cards_supported: 'zolotaya-korona',
  consumer: 'mkk',
  'credit-card': 'zolotaya-korona',
  credit: 'credits',
  crypto: 'bitcoin',
  currency: 'mkk',
  currency_1: 'KGS',
  currency_13: 'CNY',
  currency_2: 'USD',
  currency_3: 'EUR',
  currency_4: 'RUB',
  currency_5: 'KZT',
  danger: 'octagon-alert',
  'exchange-rates': 'chart-column-increasing',
  farm_development: 'tractor',
  fence: 'property',
  forex: 'chart-candlestick',
  free: 'hand-helping',
  green: 'leafy-green',
  handshake: 'mka',
  innovations: 'laptop-minimal-check',
  installment: 'calendar-clock',
  investments: 'employer',
  kyc: 'mka',
  lawyer: 'scale',
  leasing: 'calendar-1',
  legislation: 'scroll-text',
  medicine: 'medical',
  'money-transfers': 'arrow-left-right',
  mortgage: 'building',
  'no-guarantee': 'hand-helping',
  'no-profit': 'banknote-x',
  office: 'building',
  online: 'virtual',
  osago: 'car',
  other: 'termination',
  outboard: 'plane-takeoff',
  overdraft: 'wallet-cards',
  passengers: 'person-standing',
  professional: 'user-pen',
  realestate_support: 'house',
  'refresh-ccw': 'transfer',
  remote: 'laptop-minimal-check',
  salary: 'employer',
  'sidebar-rates': 'chart-column-increasing',
  syringe: 'medical',
  'tax-service': 'calculator',
  tourism: 'pyramid',
  traveling: 'plane',
  truck: 'transport',
}

// Icons are loaded as raw SFC source: string modules have no imports, so each icon can get
// its own chunk (see manualChunks in nuxt.config.ts) without pulling the Vue runtime along
const modules = import.meta.glob<string>('./icons/*.vue', { query: '?raw', import: 'default' })
const cache = new Map<string, ReturnType<typeof defineAsyncComponent>>()

const SVG_RE = /<svg\b([^>]*)>([\s\S]*)<\/svg>/
const ATTR_RE = /([\w:-]+)="([^"]*)"/g

function toComponent(source: string) {
  const [, rawAttrs = '', body = ''] = source.match(SVG_RE) ?? []
  const attrs: Record<string, string> = {}
  for (const [, key, value] of rawAttrs.matchAll(ATTR_RE)) {
    if (key && value !== undefined) attrs[key] = value
  }
  return () => h('svg', { ...attrs, innerHTML: body })
}

function makeIcon(name: string) {
  const resolved = aliases[name] ?? name
  if (cache.has(resolved)) return cache.get(resolved)!
  const loader = modules[`./icons/${resolved}.vue`]
  if (!loader) return null
  const component = defineAsyncComponent(async () => toComponent(await loader()))
  cache.set(resolved, component)
  return component
}

const Icon = shallowRef(makeIcon(props.name))

// Raw modules are not SFCs, so plugin-vue does not report them to the SSR context. Without
// this Nuxt emits no modulepreload for the icon chunk, and hydration waits for its fetch.
if (import.meta.server) {
  const resolved = aliases[props.name] ?? props.name
  const ssrContext = useSSRContext<{ modules?: Set<string> }>()
  if (ssrContext && modules[`./icons/${resolved}.vue`]) {
    (ssrContext.modules ??= new Set()).add(`components/ui/icon/icons/${resolved}.vue?raw`)
  }
}

watch(() => props.name, (name) => {
  Icon.value = makeIcon(name)
})
</script>

<template>
  <component :is="Icon" />
</template>
